import { Field, Select, Switch } from '@bb/ui';
import { Fragment, useState, type ReactNode } from 'react';
import { TranslationStatusBadge } from './TranslationStatusBadge.tsx';
import {
  emptyFields,
  environmentLocales,
  humanizePath,
  isRtlLocale,
  previewContent,
  readPath,
  textFieldPaths,
  translationStatusOf,
  type ChannelContent,
  type LocaleSettings,
} from './localization.ts';

/** Draws merged content, e.g. the channel's phone or card preview. */
export type RenderLocalizedContent = (content: ChannelContent, locale: string) => ReactNode;

export interface MultiLanguagePreviewProps {
  /** The environment's locales. */
  locales: LocaleSettings;
  /** One channel content with `localized` and `translationStatus`. */
  content: ChannelContent;
  /** Draws the merged content of a locale; by default its text fields. */
  render?: RenderLocalizedContent;
  /** Text fields shown by the default rendering, as dot paths; by default every text field. */
  fields?: readonly string[];
  /** Field labels by path; by default the path in words. */
  labels?: Readonly<Record<string, string>>;
}

export interface LocalePreviewProps extends MultiLanguagePreviewProps {
  /** Locale shown first; the default locale by default. */
  initialLocale?: string;
}

function TextFields({ content, fields, labels }: { content: ChannelContent; fields: readonly string[]; labels: Readonly<Record<string, string>> }) {
  return (
    <dl className="facts">
      {fields.map((path) => {
        const value = readPath(content, path);
        return (
          <Fragment key={path}>
            <dt>{labels[path] ?? humanizePath(path)}</dt>
            <dd>{typeof value === 'string' && value ? value : '—'}</dd>
          </Fragment>
        );
      })}
    </dl>
  );
}

/** What a profile of one locale sees, with where the text comes from when the locale has no translation of its own. */
function LocaleCard({ locale, locales, content, render, fields, labels }: { locale: string } & Required<Pick<MultiLanguagePreviewProps, 'locales' | 'content' | 'labels'>> & { render?: RenderLocalizedContent; fields: readonly string[] }) {
  const { defaultLocale } = environmentLocales(locales);
  const merged = previewContent(content, locale, locales);
  const isDefault = locale === defaultLocale;
  const status = translationStatusOf(content, locale);
  return (
    <article className="locale-preview" aria-label={`Preview: ${locale}`} lang={merged.locale} dir={isRtlLocale(merged.locale) ? 'rtl' : undefined}>
      <h4 dir="ltr">
        {locale} {isDefault ? <span className="hint">default</span> : <TranslationStatusBadge status={status} incomplete={status === 'ready' && emptyFields(content, locale, fields).length > 0} />}
      </h4>
      {!isDefault && merged.locale !== locale && (
        <p className="hint" dir="ltr">
          No {locale} translation: shown {merged.locale === defaultLocale ? `in the default (${defaultLocale})` : `in ${merged.locale}`}.
        </p>
      )}
      {render ? render(merged.content, merged.locale) : <TextFields content={merged.content} fields={fields} labels={labels} />}
    </article>
  );
}

/** Every locale side by side («multi-language» preview), the default first (BB-55). */
export function MultiLanguagePreview({ locales, content, render, fields, labels = {} }: MultiLanguagePreviewProps) {
  const { codes } = environmentLocales(locales);
  const paths = fields ?? textFieldPaths(content);
  return (
    <div className="locale-preview-grid" role="group" aria-label="All locales">
      {codes.map((locale) => (
        <LocaleCard key={locale} locale={locale} locales={locales} content={content} render={render} fields={paths} labels={labels} />
      ))}
    </div>
  );
}

/**
 * Preview of a message by locale (BB-55): the content as a profile of the chosen locale gets it — its translation,
 * else the first fallback that has one, else the default — or every locale side by side.
 */
export function LocalePreview({ locales, content, render, fields, labels = {}, initialLocale }: LocalePreviewProps) {
  const { defaultLocale, codes } = environmentLocales(locales);
  const [locale, setLocale] = useState(initialLocale ?? defaultLocale);
  const [all, setAll] = useState(false);
  const shown = codes.includes(locale) ? locale : defaultLocale;
  const paths = fields ?? textFieldPaths(content);
  return (
    <section className="panel" aria-label="Preview by locale">
      <div className="filters">
        <Field label="Preview as">
          <Select options={codes.map((c) => ({ value: c, label: c === defaultLocale ? `${c} (default)` : c }))} value={shown} disabled={all} onChange={(e) => setLocale(e.target.value)} />
        </Field>
        <Switch label="Multi-language: all locales side by side" checked={all} onCheckedChange={setAll} />
      </div>
      {all ? (
        <MultiLanguagePreview locales={locales} content={content} render={render} fields={paths} labels={labels} />
      ) : (
        <LocaleCard locale={shown} locales={locales} content={content} render={render} fields={paths} labels={labels} />
      )}
    </section>
  );
}
